<template>
  <!-- Convite de ativação de push.
       Era um banner (PushEnableInvite) pendurado como filho direto de `.layout`,
       que é `display: flex` em LINHA e `height: 100vh`. Sem largura própria, o
       banner virava uma coluna espremida ao lado do conteúdo — quebrado em
       qualquer tela, insuportável no celular, e nenhuma media query resolvia
       porque `.layout` nunca vira `column`. BaseModal teleporta para o `body`,
       então não disputa espaço com o layout: o defeito some por construção.

       Desktop ou app já instalado; no mobile não-instalado o convite de
       instalação fala primeiro (iOS exige instalar antes — TK-5 da PRD-022). -->
  <BaseModal
    :model-value="shouldInvite"
    size="medium"
    title="Ativar notificações?"
    data-test="push-invite-modal"
    @update:model-value="onFechar"
  >
    <div class="push-invite-body">
      <div class="push-invite-icon">
        <i class="fas fa-bell"></i>
      </div>
      <p class="push-invite-lead">
        Receba alertas da fila de atendimento no seu aparelho, mesmo com a aba
        fechada.
      </p>
    </div>

    <template #footer>
      <!-- Recusa à esquerda, ação à direita: a ordem canônica do sistema. As
           três saídas ficam visíveis juntas de propósito — esconder a definitiva
           atrás de um "avançado" seria manter a insistência com outro nome.
           O `.modal-footer` do BaseModal é flex em LINHA: sem este wrapper em
           coluna, o opt-out vira uma quarta coluna espremida ao lado dos botões
           e a fileira estoura a largura do modal pela esquerda. -->
      <div class="push-invite-actions">
      <div class="push-invite-footer">
        <div class="push-invite-defers">
          <BaseButton
            variant="ghost"
            data-test="push-invite-later-btn"
            @click="onAdiar('later')"
          >
            Mais tarde
          </BaseButton>
          <BaseButton
            variant="ghost"
            data-test="push-invite-today-btn"
            @click="onAdiar('today')"
          >
            Hoje não
          </BaseButton>
        </div>
        <BaseButton
          variant="primary"
          icon="fas fa-bell"
          :loading="busy"
          data-test="push-invite-enable-btn"
          @click="onAtivar"
        >
          Ativar
        </BaseButton>
      </div>
      <button
        type="button"
        class="push-invite-optout"
        data-test="push-invite-optout-btn"
        @click="onNuncaMais"
      >
        Não quero ser lembrado
      </button>
      </div>
    </template>
  </BaseModal>
</template>

<script setup>
import { usePushNotifications } from "@/composables/usePushNotifications";
import BaseModal from "@/components/ui/BaseModal.vue";
import BaseButton from "@/components/ui/BaseButton.vue";

const { shouldInvite, busy, enable, deferInvite, optOutInvite } =
  usePushNotifications();

function onAtivar() {
  enable();
}

function onAdiar(escopo) {
  deferInvite(escopo);
}

function onNuncaMais() {
  optOutInvite();
}

// Fechar no X, no overlay ou no Esc é "não decidi agora", não "nunca mais" —
// então vale como o adiamento mais curto. Sem isto o modal reabriria na próxima
// navegação, que é exatamente a insistência que este trabalho corrige.
function onFechar(aberto) {
  if (!aberto) deferInvite("later");
}
</script>

<style scoped>
.push-invite-body {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.push-invite-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: var(--radius-md);
  background: var(--warning-500, #f59e0b);
  color: white;
}

.push-invite-lead {
  margin: 0;
  color: var(--text-secondary);
  font-size: var(--font-sm);
}

.push-invite-actions {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.push-invite-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  width: 100%;
}

.push-invite-defers {
  display: flex;
  gap: var(--space-2);
}

.push-invite-optout {
  width: 100%;
  margin-top: var(--space-3);
  border: none;
  background: none;
  color: var(--text-secondary);
  font-size: var(--font-xs);
  text-decoration: underline;
  cursor: pointer;
}

.push-invite-optout:hover {
  color: var(--text-primary);
}

/* No celular o rodapé empilha: três botões lado a lado em 360px viram alvos
   menores que os 44px do WCAG 2.5.5. */
@media (max-width: 480px) {
  .push-invite-footer {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .push-invite-defers {
    justify-content: space-between;
  }
}
</style>
